import { NotificationBars } from 'presentation/ui/components/notification-bars/NotificationBars';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { L10nContext } from 'context/L10nContext';

import { Guid } from 'lib/guid/Guid';

import { ClientContext } from 'services/core/context/ClientContext';
import { FacilityContext } from 'services/core/context/FacilityContext';
import { Interaction } from 'services/device/domain/business/common/interaction/Interaction';
import { Sequence } from 'services/device/domain/business/common/sequence/Sequence';
import { FieldType } from 'services/device/domain/business/common/field/FieldType';
import { Violation } from 'services/device/domain/business/common/violation/Violation';
import { getDeviceTypeDefinition } from 'services/device/domain/business/inventory/DeviceTypeDefinition';
import { getIntervalDefinition } from 'services/device/domain/business/inventory/IntervalDefinition';
import { Interval } from 'services/device/domain/business/inventory/Interval';
import { SequenceType } from 'services/device/domain/business/inventory/SequenceType';
import { getSequenceDefinition, getSequenceTypeDefinition } from 'services/device/domain/business/inventory/SequenceTypeDefinition';
import { InventoryFactory } from 'services/device/domain/business/util/InventoryFactory';
import { getCreateSequenceInteraction } from 'services/device/domain/business/util/SequenceUtil';
import { DeviceViewModel } from 'services/device/domain/model/DeviceModel';
import { WidgetsRenderer } from 'services/device/presentation/ui/widget/WidgetsRenderer';
import { createSequence } from 'services/device/store/sequenceSlice';
import { Mode } from 'services/device/domain/business/common/misc/Mode';

import { NotificationBar } from 'presentation/ui/components/notification-bar/NotificationBar';
import { NotificationLevel } from 'presentation/ui/components/notification-bar/NotificationLevel';
import { ColumnLayoutSection } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSection';
import { ColumnLayoutSectionMode } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSectionMode';
import { ColumnLayout, ColumnLayoutMode } from 'presentation/ui/layouts/column-layout/ColumnLayout';
import { FormLayoutSection } from 'presentation/ui/layouts/form-layout/form-layout-section/FormLayoutSection';
import { FormLayout } from 'presentation/ui/layouts/form-layout/FormLayout';
import { FormLayoutSectionAlign } from 'presentation/ui/layouts/form-layout/FormLayoutSectionAlign';
import { ButtonPrimary } from 'presentation/ui/partials/button/button-primary/ButtonPrimary';
import { ButtonSecondary } from 'presentation/ui/partials/button/button-secondary/ButtonSecondary';
import { Label } from 'presentation/ui/partials/input/label/Label';
import { Select, SelectOption } from 'presentation/ui/partials/input/select/Select';
import { getSequenceCalculationFormula, SEQUENCE_CALCULATION_FORMULAS } from './sequence-calculation-formulas';

import './sequence-create-form.scss';

type SequenceSummaryRow = {
	label: string;
	value: string;
};

type SequenceSummaryEntry = {
	label: string;
	calculated: boolean;
};

export interface SequenceCreateFormProps {
	device: DeviceViewModel;
	onError: (error: Error) => void;
}

/**
 * The sequence create form
 */
export const SequenceCreateForm = (props: SequenceCreateFormProps): JSX.Element => {

	const { device, onError } = props;

	// Consume the context
	const clientContext = useContext(ClientContext);
	const facilityContext = useContext(FacilityContext);
	const l10nContext = useContext(L10nContext);

	// Consume the dispatch object
	const dispatch = useDispatch();

	// Create state for the sequence interactions
	const [sequenceType, setSequenceType] = useState<SequenceType>(null);
	const [sequenceInteraction, setSequenceInteraction] = useState<Interaction<Sequence>>(null);
	const [validationViolations, setValidationViolations] = useState<ReadonlyArray<Violation>>([]);

	// Create an interaction if device is available, sequence type defined and no interaction was already created
	useEffect(() => {
		const prepareInteraction = async (): Promise<void> => {
			// Select a random device
			setSequenceInteraction(getCreateSequenceInteraction(device, sequenceType));
		};
		if (sequenceType !== null && sequenceInteraction === null) {
			void prepareInteraction();
		}
	}, [clientContext, device, sequenceType, sequenceInteraction]);

	const handleTypeSelect = (selectedSequenceType: string): void => {
		setSequenceType(selectedSequenceType as SequenceType);
	};

	const handlePrev = (): void => {
		try {
			setValidationViolations([]);
			setSequenceInteraction(sequenceInteraction.previous());
		} catch (error) {
			onError(error);
		}
	};

	const handleNext = (): void => {
		try {
			const validationErrors = sequenceInteraction.validate();
			if (validationErrors.length > 0) {
				setValidationViolations(validationErrors);
			} else {
				setValidationViolations([]);
				setSequenceInteraction(sequenceInteraction.next());
			}
		} catch (error) {
			onError(error);
		}
	};

	const handleSave = (): void => {
		const validationErrors = sequenceInteraction.validate();
		if (validationErrors.length > 0) {
			setValidationViolations(validationErrors);
		} else {
			setValidationViolations([]);
			try {
				const sequence = sequenceInteraction.finish();
				const sequenceViewData = sequence.toViewData();

				dispatch(createSequence({
					Client: clientContext.selectedClientUuid,
					Facility: facilityContext.selectedFacilityUuid,
					Device: device.Uuid,
					DeviceType: device.Type,
					Type: sequenceType,
					Name: sequenceViewData.values.name,
					NotificationMailAddresses: [],
					SequenceConfiguration: sequenceViewData
				}));
			} catch (error) {
				onError(error);
			}
		}
	};

	const handleFormSubmit = (): void => {
		if (sequenceInteraction === null) {
			return;
		}
		if (sequenceInteraction.isLast()) {
			handleSave();
		} else {
			handleNext();
		}
	};

	const renderValidationViolations = (): Array<JSX.Element> => {
		return validationViolations.map<JSX.Element>((violation) => {
			return (
				<NotificationBar
					key={violation.getMessageKey()}
					message={l10nContext.translate(violation.getMessageKey())}
					level={NotificationLevel.LEVEL_ERROR}
				/>
			);
		});
	};

	const renderSequenceTypeInteraction = (): JSX.Element => {
		if (sequenceType !== null) {
			return null;
		}

		const inventory = InventoryFactory.createInvetory(clientContext.permittedProductGroups());
		const deviceTypeDefinition = getDeviceTypeDefinition(device.Type);
		const sequenceTypeDefinitions = inventory.getSequenceTypeDefinitions(deviceTypeDefinition);

		const filteredSequenceTypeDefinitions = sequenceTypeDefinitions.filter((filteredTypeDefinition) => {
			return !filteredTypeDefinition.isDeprecated();
		});

		const selectOptions = filteredSequenceTypeDefinitions.map<SelectOption>((sequenceTypeDefinition) => {
			return {
				label: l10nContext.translate(sequenceTypeDefinition.getLabelKey()),
				value: sequenceTypeDefinition.getSequenceType()
			};
		});
		return (
			<Label label={l10nContext.translate('sequence.attributes.type', 'Typ')}>
				<Select options={selectOptions} onchange={handleTypeSelect} />
			</Label>
		);
	};

	const splitTopLevelArgs = (value: string): Array<string> => {
		const args: Array<string> = [];
		let current = '';
		let depth = 0;

		for (const character of value) {
			if (character === '(') {
				depth += 1;
				current += character;
			} else if (character === ')') {
				depth -= 1;
				current += character;
			} else if (character === ',' && depth === 0) {
				args.push(current.trim());
				current = '';
			} else {
				current += character;
			}
		}

		if (current.trim().length > 0) {
			args.push(current.trim());
		}

		return args;
	};

	const getFormulaParts = (expression: string): { name: string; args: Array<string> } | null => {
		const trimmedExpression = expression.trim();
		const openBracket = trimmedExpression.indexOf('(');

		if (openBracket <= 0 || !trimmedExpression.endsWith(')')) {
			return null;
		}

		const name = trimmedExpression.slice(0, openBracket).trim();
		const innerExpression = trimmedExpression.slice(openBracket + 1, -1);
		return {
			name,
			args: splitTopLevelArgs(innerExpression)
		};
	};

	const translateFormulaToken = (token: string): string => {
		return l10nContext.translate(`inventory.field.${token}`, token);
	};

	type FormulaRenderResult = {
		text: string;
		precedence: number;
	};

	const PRECEDENCE_ADD_SUBTRACT = 1;
	const PRECEDENCE_MULTIPLY_DIVIDE = 2;
	const PRECEDENCE_ATOM = 3;

	const withOptionalParentheses = (value: FormulaRenderResult, shouldWrap: boolean): string => {
		return shouldWrap ? `(${value.text})` : value.text;
	};

	const renderFormula = (
		expression: string,
		formulasByField: Readonly<Record<string, string>>,
		visitedFields: Set<string>
	): FormulaRenderResult => {
		const trimmedExpression = expression.trim();
		if (trimmedExpression.length === 0) {
			return { text: '', precedence: PRECEDENCE_ATOM };
		}

		if (/^-?\d+(\.\d+)?$/.test(trimmedExpression)) {
			return { text: trimmedExpression, precedence: PRECEDENCE_ATOM };
		}

		const formulaParts = getFormulaParts(trimmedExpression);
		if (formulaParts === null) {
			if (
				trimmedExpression === 'expectedValue'
				&& formulasByField[trimmedExpression] !== undefined
				&& !visitedFields.has(trimmedExpression)
			) {
				const nextVisitedFields = new Set<string>(visitedFields);
				nextVisitedFields.add(trimmedExpression);
				return renderFormula(formulasByField[trimmedExpression], formulasByField, nextVisitedFields);
			}

			return {
				text: translateFormulaToken(trimmedExpression),
				precedence: PRECEDENCE_ATOM
			};
		}

		const renderedArgs = formulaParts.args.map((arg) => renderFormula(arg, formulasByField, new Set<string>(visitedFields)));

		switch (formulaParts.name) {
			case 'Literal':
				return { text: renderedArgs[0]?.text ?? '', precedence: PRECEDENCE_ATOM };
			case 'Percent': {
				const left = renderedArgs[0] ?? { text: '', precedence: PRECEDENCE_ATOM };
				const right = renderedArgs[1] ?? { text: '', precedence: PRECEDENCE_ATOM };
				return {
					text: `${withOptionalParentheses(left, left.precedence < PRECEDENCE_MULTIPLY_DIVIDE)} / ${withOptionalParentheses(right, right.precedence <= PRECEDENCE_MULTIPLY_DIVIDE)}`,
					precedence: PRECEDENCE_MULTIPLY_DIVIDE
				};
			}
			case 'Difference': {
				const left = renderedArgs[0] ?? { text: '', precedence: PRECEDENCE_ATOM };
				const right = renderedArgs[1] ?? { text: '', precedence: PRECEDENCE_ATOM };
				return {
					text: `${withOptionalParentheses(left, left.precedence < PRECEDENCE_ADD_SUBTRACT)} - ${withOptionalParentheses(right, right.precedence <= PRECEDENCE_ADD_SUBTRACT)}`,
					precedence: PRECEDENCE_ADD_SUBTRACT
				};
			}
			case 'Sum':
				return {
					text: renderedArgs
						.map((arg) => withOptionalParentheses(arg, arg.precedence < PRECEDENCE_ADD_SUBTRACT))
						.join(' + '),
					precedence: PRECEDENCE_ADD_SUBTRACT
				};
			case 'Product':
				return {
					text: renderedArgs
						.map((arg) => withOptionalParentheses(arg, arg.precedence < PRECEDENCE_MULTIPLY_DIVIDE))
						.join(' * '),
					precedence: PRECEDENCE_MULTIPLY_DIVIDE
				};
			default:
				return {
					text: `${formulaParts.name}(${renderedArgs.map((arg) => arg.text).join(', ')})`,
					precedence: PRECEDENCE_ATOM
				};
		}
	};

	const toReadableFormula = (
		expression: string,
		formulasByField: Readonly<Record<string, string>>,
		visitedFields: Set<string>
	): string => {
		return renderFormula(expression, formulasByField, visitedFields).text;
	};

	const getSequenceTypeSummaryRows = (): Array<SequenceSummaryRow> => {
		const sequenceTypeDefinition = getSequenceTypeDefinition(sequenceType);
		const selectedTypeLabel = l10nContext.translate(sequenceTypeDefinition.getLabelKey());

		const parametersByName: Record<string, SequenceSummaryEntry> = {};
		const thresholdsByName: Record<string, SequenceSummaryEntry> = {};
		const calculationMethodsByName: Record<string, SequenceSummaryEntry> = {};

		const excludedParameterNames = new Set<string>([
			'name',
			'note',
			'comment',
			'ident',
			'notificationMailAddresses',
			'referenceDate'
		]);

		const addUniqueEntry = (
			target: Record<string, SequenceSummaryEntry>,
			key: string,
			label: string,
			calculated = false
		): void => {
			if (target[key] === undefined) {
				target[key] = { label, calculated };
				return;
			}

			if (calculated) {
				target[key].calculated = true;
			}
		};

		for (const detailEntry of sequenceTypeDefinition.getDetailsDescription().entries) {
			const excludedByName = excludedParameterNames.has(detailEntry.name);
			const excludedByType = (
				detailEntry.type === FieldType.COMMENT
				|| detailEntry.type === FieldType.MAIL_ADDRESS
				|| detailEntry.type === FieldType.TEXT
				|| detailEntry.type === FieldType.URL
			);

			if (!excludedByName && !excludedByType) {
				addUniqueEntry(
					parametersByName,
					detailEntry.name,
					l10nContext.translate(detailEntry.labelKey),
					detailEntry.stored === false
				);
			}
		}

		for (const tableDescription of sequenceTypeDefinition.getTableDescriptions()) {
			const thresholdDescriptions = [
				tableDescription.lowerReactionValue,
				tableDescription.upperReactionValue,
				tableDescription.lowerToleranceValue,
				tableDescription.upperToleranceValue
			];

			for (const thresholdDescription of thresholdDescriptions) {
				if (thresholdDescription !== undefined) {
					addUniqueEntry(
						thresholdsByName,
						thresholdDescription.name,
						l10nContext.translate(thresholdDescription.labelKey),
						thresholdDescription.stored === false
					);
				}
			}

			const calculationDescriptions = [
				tableDescription.deviation,
				tableDescription.expectedValue,
				tableDescription.value,
				...(tableDescription.additionalColumns ?? [])
			];

			for (const calculationDescription of calculationDescriptions) {
				if (calculationDescription !== undefined && calculationDescription.stored === false) {
					addUniqueEntry(
						calculationMethodsByName,
						calculationDescription.name,
						l10nContext.translate(calculationDescription.labelKey),
						true
					);
				}
			}
		}

		for (const chartDescription of sequenceTypeDefinition.getChartDescriptions()) {
			const thresholdDescriptions = [
				chartDescription.lowerReactionValue,
				chartDescription.upperReactionValue,
				chartDescription.lowerToleranceValue,
				chartDescription.upperToleranceValue
			];

			for (const thresholdDescription of thresholdDescriptions) {
				if (thresholdDescription !== undefined) {
					addUniqueEntry(
						thresholdsByName,
						thresholdDescription.name,
						l10nContext.translate(thresholdDescription.labelKey),
						thresholdDescription.stored === false
					);
				}
			}

			const calculationDescriptions = [
				chartDescription.deviation,
				chartDescription.expectedValue,
				chartDescription.value
			];

			for (const calculationDescription of calculationDescriptions) {
				if (calculationDescription !== undefined && calculationDescription.stored === false) {
					addUniqueEntry(
						calculationMethodsByName,
						calculationDescription.name,
						l10nContext.translate(calculationDescription.labelKey),
						true
					);
				}
			}
		}

		Object.keys(thresholdsByName).forEach((thresholdName) => {
			delete calculationMethodsByName[thresholdName];
		});

		const sequenceDefinition = getSequenceDefinition(device.Type, sequenceType);
		const defaultSequenceValues = sequenceDefinition.createSequence(Mode.USER).toViewData().values;
		Object.keys(defaultSequenceValues).forEach((parameterName) => {
			const isExcludedParameter = excludedParameterNames.has(parameterName);
			const isThreshold = thresholdsByName[parameterName] !== undefined;
			const isCalculationMethod = calculationMethodsByName[parameterName] !== undefined;

			if (!isExcludedParameter && !isThreshold && !isCalculationMethod) {
				const parameterLabel = l10nContext.translate(`inventory.field.${parameterName}`, parameterName);
				addUniqueEntry(parametersByName, parameterName, parameterLabel);
			}
		});

		const notAvailableText = l10nContext.translate('view.device.modal.action.createSequence.summary.notAvailable', 'N/A');
		const calculatedText = l10nContext.translate('view.device.modal.action.createSequence.summary.calculated', 'Automatisch berechnet');
		const formulasByField = SEQUENCE_CALCULATION_FORMULAS[sequenceType] ?? {};

		const formatStoredValue = (value: unknown): string => {
			if (value === undefined || value === null || value === '') {
				return notAvailableText;
			}

			if (Array.isArray(value)) {
				const formattedItems = value
					.map((entry) => formatStoredValue(entry))
					.filter((entry) => entry !== notAvailableText);
				return formattedItems.length > 0 ? formattedItems.join(', ') : notAvailableText;
			}

			if (typeof value === 'string') {
				if ((Object.values(Interval) as Array<string>).includes(value)) {
					return l10nContext.translate(getIntervalDefinition(value as Interval).getLabelKey(), value);
				}
				return value;
			}

			if (typeof value === 'number' || typeof value === 'boolean') {
				return String(value);
			}

			return JSON.stringify(value);
		};

		const buildLabelsWithStoredValues = (entries: Record<string, SequenceSummaryEntry>): Array<string> => {
			return Object.keys(entries)
				.sort((firstKey, secondKey) => firstKey.localeCompare(secondKey))
				.map((entryKey) => {
					const entry = entries[entryKey];
					const formula = entry.calculated
						? getSequenceCalculationFormula(sequenceType, entryKey)
						: null;
					let value = formatStoredValue(defaultSequenceValues[entryKey]);
					if (entry.calculated) {
						value = formula !== null
							? toReadableFormula(formula, formulasByField, new Set<string>([entryKey]))
							: calculatedText;
					}

					if (value === notAvailableText) {
						return null;
					}

					const label = entry.label;
					return `${label}: ${value}`;
				})
				.filter((entry): entry is string => entry !== null);
		};

		const parameterLabels = buildLabelsWithStoredValues(parametersByName);
		const thresholdLabels = buildLabelsWithStoredValues(thresholdsByName);
		const calculationMethodLabels = buildLabelsWithStoredValues(calculationMethodsByName);

		const summaryRows: Array<SequenceSummaryRow> = [
			{
				label: l10nContext.translate('view.device.modal.action.createSequence.summary.type', 'Typ'),
				value: selectedTypeLabel
			}
		];

		if (parameterLabels.length > 0) {
			summaryRows.push({
				label: l10nContext.translate('view.device.modal.action.createSequence.summary.parameters', 'Parameter'),
				value: parameterLabels.join(', ')
			});
		}

		summaryRows.push({
			label: l10nContext.translate('view.device.modal.action.createSequence.summary.thresholds', 'Grenzwerte'),
			value: thresholdLabels.length > 0 ? thresholdLabels.join('\n') : notAvailableText
		});

		summaryRows.push({
			label: l10nContext.translate('view.device.modal.action.createSequence.summary.calculationMethods', 'Berechnungsmethode'),
			value: calculationMethodLabels.length > 0 ? calculationMethodLabels.join('\n') : notAvailableText
		});

		return summaryRows;
	};

	const renderSequenceTypeSummary = (): JSX.Element | null => {
		if (sequenceType === null) {
			return null;
		}

		const renderSummaryValue = (value: string): Array<ReactNode> => {
			const subscriptMap: Record<string, string> = {
				'₁': '1',
				'₂': '2'
			};

			const renderedParts: Array<ReactNode> = [];
			let offset = 0;

			value
				.split(/([₁₂])/g)
				.filter((part) => part.length > 0)
				.forEach((part) => {
					const key = `${offset}-${part}`;
					const mappedValue = subscriptMap[part];
					if (mappedValue !== undefined) {
						renderedParts.push(
							<sub className="sequence-create-form__summary__row__value__subscript" key={key}>
								{mappedValue}
							</sub>
						);
					} else {
						renderedParts.push(
							<span key={key}>
								{part}
							</span>
						);
					}
					offset += part.length;
				});

			return renderedParts;
		};

		const summaryRows = getSequenceTypeSummaryRows();

		return (
			<div className="sequence-create-form__summary">
				{summaryRows.map((summaryRow) => (
					<div className="sequence-create-form__summary__row" key={summaryRow.label}>
						<span className="sequence-create-form__summary__row__label">{summaryRow.label}:</span>
						<span className="sequence-create-form__summary__row__value">{renderSummaryValue(summaryRow.value)}</span>
					</div>
				))}
				<div className="sequence-create-form__summary__row" aria-hidden="true" />
			</div>
		);
	};

	const renderSequenceCreateInteraction = (): JSX.Element => {
		if (sequenceInteraction === null) {
			return null;
		}

		return (
			<WidgetsRenderer key="widgets" widgets={sequenceInteraction.getWidgets()} />
		);
	};

	const renderSequenceCreateButtons = (): JSX.Element => {
		if (sequenceInteraction === null) {
			return null;
		}

		let leftButton = null;
		if (!sequenceInteraction.isFirst()) {
			leftButton = (
				<ButtonSecondary
					buttonText={l10nContext.translate('common.button.back', 'Zurück')}
					onClick={handlePrev}
				/>
			);
		}

		let rightButton;
		if (sequenceInteraction.isLast()) {
			rightButton = (
				<ButtonPrimary
					buttonText={l10nContext.translate('common.button.save', 'Speichern')}
					type="submit"
				/>
			);
		} else {
			rightButton = (
				<ButtonPrimary
					buttonText={l10nContext.translate('common.button.next', 'Weiter')}
					type="submit"
				/>
			);
		}

		return (
			<FormLayoutSection align={FormLayoutSectionAlign.ALIGN_BOTTOM}>
				<ColumnLayout mode={ColumnLayoutMode.ALIGN_RIGHT}>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FLEX}>
						{leftButton}
					</ColumnLayoutSection>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FIXED}>
						{rightButton}
					</ColumnLayoutSection>
				</ColumnLayout>
			</FormLayoutSection>
		);
	};

	return (
		<FormLayout align={FormLayoutSectionAlign.ALIGN_HORIZONTAL} key={Guid.generate()} onSubmit={handleFormSubmit}>
			<NotificationBars>
				{renderValidationViolations()}
			</NotificationBars>
			{renderSequenceTypeInteraction()}
			{renderSequenceTypeSummary()}
			{renderSequenceCreateInteraction()}
			{renderSequenceCreateButtons()}
		</FormLayout>
	);
};
